import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ViolationsByRule } from "../hooks/useAlertEvaluation";
import AlertRuleForm from "./AlertRuleForm";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { SavedFilterRef } from "@/hooks/useFocusGridRow";
import { AlertConditionType, AlertRule, AlertSeverity } from "@/models/AlertRule";
import { AlertDisplayMode } from "@/models/DataGridSettingsAddInData";
import { DeviceStatusOverviewData } from "@/models/DeviceStatusOverviewData";

const severityColor: Record<AlertSeverity, string> = {
  info: "#2196f3",
  warning: "#ff9800",
  critical: "#f44336",
};

function conditionSummary(rule: AlertRule): string {
  const { displayName, conditionType, threshold, thresholdMin, thresholdMax } = rule;
  const summaryMap: Record<AlertConditionType, string> = {
    above: `${displayName} > ${threshold}`,
    below: `${displayName} < ${threshold}`,
    between: `${displayName} ${thresholdMin}\u2013${thresholdMax}`,
    outside: `${displayName} < ${thresholdMin} or > ${thresholdMax}`,
    equals: `${displayName} = ${threshold}`,
    changes: `${displayName} changes`,
  };
  return summaryMap[conditionType];
}

interface AlertRulesDialogProps {
  open: boolean;
  onClose: () => void;
  violationsByRule: ViolationsByRule;
  data: DeviceStatusOverviewData[];
  savedFilterRef?: SavedFilterRef;
}

const AlertRulesDialog: React.FC<AlertRulesDialogProps> = ({ open, onClose, violationsByRule, data, savedFilterRef }) => {
  const { t } = useTranslation();
  const {
    alertRules,
    setAlertRules,
    saveGridState,
    apiRef,
    alertDisplayMode,
    setAlertDisplayMode,
    alertAutoDismiss,
    setAlertAutoDismiss,
  } = useDataGridSettings();
  const [editingRule, setEditingRule] = useState<AlertRule | undefined>(undefined);
  const [isCreating, setIsCreating] = useState(false);
  const needsSaveRef = useRef(false);

  const isEditing = editingRule !== undefined || isCreating;

  useEffect(() => {
    if (needsSaveRef.current) {
      needsSaveRef.current = false;
      saveGridState();
    }
  }, [alertRules, alertDisplayMode, alertAutoDismiss, saveGridState]);

  const handleDisplayModeChange = (_: unknown, value: AlertDisplayMode | null) => {
    if (!value) return;
    setAlertDisplayMode(value);
    needsSaveRef.current = true;
  };

  const handleAutoDismissChange = (_: unknown, checked: boolean) => {
    setAlertAutoDismiss(checked);
    needsSaveRef.current = true;
  };

  const enterEditMode = (rule: AlertRule) => {
    setEditingRule(rule);
    setIsCreating(false);
  };

  const enterCreateMode = () => {
    setEditingRule(undefined);
    setIsCreating(true);
  };

  const exitEditMode = () => {
    setEditingRule(undefined);
    setIsCreating(false);
  };

  const handleToggleEnabled = (ruleId: string) => {
    setAlertRules(prev => prev.map(r => (r.id === ruleId ? { ...r, enabled: !r.enabled } : r)));
    needsSaveRef.current = true;
  };

  const handleDelete = (ruleId: string) => {
    setAlertRules(prev => prev.filter(r => r.id !== ruleId));
    needsSaveRef.current = true;
  };

  const handleSave = (rule: AlertRule) => {
    setAlertRules(prev => {
      const index = prev.findIndex(r => r.id === rule.id);
      if (index >= 0) {
        const updated = [...prev];
        updated[index] = rule;
        return updated;
      }
      return [...prev, rule];
    });
    needsSaveRef.current = true;
    exitEditMode();
  };

  const handleShowViolations = (rule: AlertRule) => {
    const violatedDeviceIds = violationsByRule.get(rule.id);
    if (!violatedDeviceIds || violatedDeviceIds.size === 0) return;
    const violatedNames: string[] = [];
    for (const deviceId of violatedDeviceIds) {
      const row = data.find(r => r.device.id === deviceId);
      if (row) violatedNames.push(row.device.name);
    }
    if (violatedNames.length > 0) {
      if (savedFilterRef && !savedFilterRef.current) {
        savedFilterRef.current = apiRef.current?.state?.filter?.filterModel ?? null;
      }
      apiRef.current?.setFilterModel({
        items: [
          {
            field: "device.name",
            operator: "isAnyOf",
            value: violatedNames,
          },
        ],
      });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditing ? (isCreating ? t("alertRules.addRule") : t("alertRules.editRule")) : t("alertRules.title")}</DialogTitle>
      <DialogContent>
        {isEditing ? (
          <AlertRuleForm rule={editingRule} onSave={handleSave} onCancel={exitEditMode} />
        ) : (
          <>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                  {t("alertRules.displayMode")}
                </Typography>
                <ToggleButtonGroup
                  value={alertDisplayMode}
                  exclusive
                  onChange={handleDisplayModeChange}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="subtle">{t("alertRules.displayModeSubtle")}</ToggleButton>
                  <ToggleButton value="prominent">{t("alertRules.displayModeProminent")}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={alertAutoDismiss}
                    onChange={handleAutoDismissChange}
                    size="small"
                  />
                }
                label={t("alertRules.autoDismiss")}
              />
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {!alertRules || alertRules.length === 0 ? (
              <Typography color="text.secondary">{t("alertRules.noRules")}</Typography>
            ) : (
          <List disablePadding>
            {alertRules?.map(rule => {
              const violationCount = rule.enabled
                ? (violationsByRule.get(rule.id)?.size ?? 0)
                : 0;
              return (
                <ListItem
                  key={rule.id}
                  sx={{
                    borderLeft: `4px solid ${severityColor[rule.severity]}`,
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                  secondaryAction={
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Switch
                        edge="end"
                        checked={rule.enabled}
                        onChange={() => handleToggleEnabled(rule.id)}
                        size="small"
                      />
                      <IconButton size="small" onClick={() => enterEditMode(rule)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(rule.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{rule.name}</span>
                        {violationCount > 0 && (
                          <Chip
                            label={violationCount}
                            size="small"
                            sx={{
                              bgcolor: severityColor[rule.severity],
                              color: "#fff",
                              fontWeight: "bold",
                              cursor: "pointer",
                              height: 22,
                              minWidth: 28,
                            }}
                            onClick={() => handleShowViolations(rule)}
                          />
                        )}
                      </Box>
                    }
                    secondary={conditionSummary(rule)}
                    sx={{ pr: 12 }}
                  />
                </ListItem>
              );
            })}
              </List>
            )}
          </>
        )}
      </DialogContent>
      {!isEditing && (
        <DialogActions>
          <Button onClick={enterCreateMode}>{t("alertRules.addRule")}</Button>
          <Button onClick={onClose}>{t("alertRules.close")}</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default AlertRulesDialog;
